import { useSeoMeta } from '@unhead/react';
import { Link } from 'react-router-dom';
import { Terminal, Zap, Key, Package, ArrowRight, Search, Plus, Sparkles, Star, ShieldCheck, GitBranch, Code2, Eye, Network, UserCheck, Lock, Globe, Bot, Server } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { LoginArea } from '@/components/auth/LoginArea';
import { SkillBrowser } from '@/components/SkillBrowser';
import { useSkills } from '@/hooks/useSkills';
import { useAuthor } from '@/hooks/useAuthor';
import { formatPrice, type Skill } from '@/lib/skills';
import { genUserName } from '@/lib/genUserName';
import { nip19 } from 'nostr-tools';
import { useScrollReveal } from '@/hooks/useScrollReveal';

const Index = () => {
  useSeoMeta({
    title: 'SkillForge - Discover & Share AI Agent Skills',
    description: 'The open registry for AI agent skills and MCP servers. Publish, discover, and share tools built for the open agent economy.',
  });

  const { user } = useCurrentUser();

  // Scroll reveal refs — one per section below the hero
  const featuresRef   = useScrollReveal();
  const trustRef      = useScrollReveal();
  const wotRef        = useScrollReveal();
  const openSrcRef    = useScrollReveal();
  const recentRef     = useScrollReveal();
  const ctaRef        = useScrollReveal();

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 backdrop-blur-2xl bg-background/60 border-b border-white/[0.06]">
        <div className="container mx-auto px-4 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5 group">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-orange-500/20 to-emerald-500/20 flex items-center justify-center glow-primary-sm border border-white/[0.08] group-hover:border-white/[0.15] transition-all">
              <Terminal className="w-4 h-4 text-orange-400" />
            </div>
            <span className="font-semibold text-lg tracking-tight">SkillForge</span>
          </Link>

          <nav className="hidden md:flex items-center gap-8 text-sm text-muted-foreground">
            <Link to="/browse" className="hover:text-foreground transition-colors">Browse</Link>
            {user && <Link to="/my-skills" className="hover:text-foreground transition-colors">My Skills</Link>}
            <Link to="/connect-agent" className="hover:text-foreground transition-colors">Connect Agent</Link>
            <Link to="/publish" className="hover:text-foreground transition-colors opacity-50 hover:opacity-100 text-xs">Publish</Link>
          </nav>

          <div className="flex items-center gap-3">
            <LoginArea className="max-w-60" />
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="pt-32 pb-24 relative overflow-hidden">
        {/* Ambient background effects */}
        <div className="absolute inset-0 bg-gradient-to-b from-orange-500/[0.07] via-transparent to-transparent" />
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[1200px] h-[600px] bg-gradient-to-br from-orange-600/20 via-amber-500/10 to-emerald-500/10 rounded-full blur-[120px] pointer-events-none opacity-60" />
        <div className="absolute bottom-0 right-0 w-[600px] h-[400px] bg-gradient-to-tl from-emerald-600/15 to-transparent rounded-full blur-[100px] pointer-events-none" />
        
        {/* Subtle grid overlay */}
        <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.01)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.01)_1px,transparent_1px)] bg-[size:64px_64px] [mask-image:radial-gradient(ellipse_50%_50%_at_50%_50%,black,transparent)]" />

        <div className="container mx-auto px-4 relative">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2.5 px-4 py-2 rounded-full glass border border-white/[0.1] text-sm text-muted-foreground mb-8 animate-fade-in">
              <Sparkles className="w-4 h-4 text-orange-400" />
              <span>Built for OpenClaw</span>
              <span className="w-1 h-1 rounded-full bg-orange-400/50" />
              <span className="text-orange-400">Agent-native</span>
            </div>

            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tight mb-8 animate-slide-up">
              Build skills.
              <br />
              <span className="text-gradient">Power agents.</span>
            </h1>

            <p className="text-lg sm:text-xl text-muted-foreground max-w-2xl mx-auto mb-12 leading-relaxed animate-slide-up" style={{ animationDelay: '0.1s' }}>
              The open registry for AI agent skills and MCP servers.
              Publish your tools, discover what others have built, and extend the agent ecosystem.
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 animate-slide-up" style={{ animationDelay: '0.2s' }}>
              {user ? (
                <>
                  <Link to="/connect-agent">
                    <Button size="lg" className="gap-2.5 bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-400 hover:to-amber-400 border-0 shadow-xl shadow-orange-500/30 text-base px-7">
                      <Bot className="w-5 h-5" />
                      Connect Agent
                    </Button>
                  </Link>
                  <Link to="/browse">
                    <Button size="lg" variant="outline" className="gap-2 border-white/[0.1] bg-white/[0.02] hover:bg-white/[0.05] hover:border-white/[0.15] text-base">
                      <Search className="w-5 h-5" />
                      Browse Skills
                    </Button>
                  </Link>
                </>
              ) : (
                <>
                  <LoginArea />
                  <Link to="/browse">
                    <Button size="lg" variant="outline" className="gap-2 border-white/[0.1] bg-white/[0.02] hover:bg-white/[0.05] hover:border-white/[0.15] text-base">
                      <Search className="w-5 h-5" />
                      Browse Skills
                    </Button>
                  </Link>
                </>
              )}
            </div>
          </div>

          {/* Live skills preview */}
          <div className="mt-20 max-w-3xl mx-auto animate-slide-up" style={{ animationDelay: '0.3s' }}>
            <HeroSkillsPreview />
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section ref={featuresRef} className="py-24 relative">
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-orange-500/[0.02] to-transparent" />
        <div className="container mx-auto px-4 relative">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold mb-4 reveal">Why SkillForge?</h2>
            <p className="text-muted-foreground max-w-xl mx-auto reveal reveal-delay-100">
              An open, permissionless registry for agent tools — built on Nostr.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="reveal reveal-delay-100">
              <FeatureCard
                icon={<Key className="w-5 h-5" />}
                title="Your key, your identity"
                description="Your Nostr npub is your identity. Portable across every app. No one can ban it."
                gradient="from-orange-500/20 to-amber-500/20"
              />
            </div>
            <div className="reveal reveal-delay-200">
              <FeatureCard
                icon={<Package className="w-5 h-5" />}
                title="Permissionless publishing"
                description="Sign, push to relays, it's live. No review process. No gatekeepers."
                gradient="from-amber-500/20 to-yellow-500/20"
              />
            </div>
            <div className="reveal reveal-delay-300">
              <FeatureCard
                icon={<Zap className="w-5 h-5" />}
                title="Instant discovery"
                description="Tag your skills, get found. Agents and developers can search and filter by category in seconds."
                gradient="from-emerald-500/20 to-green-500/20"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Trust & Reviews Section */}
      <section ref={trustRef} className="py-28 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-emerald-500/[0.03] to-transparent" />
        <div className="absolute right-0 top-1/3 w-[500px] h-[500px] bg-gradient-to-l from-emerald-600/10 to-transparent rounded-full blur-[100px] pointer-events-none" />

        <div className="container mx-auto px-4 relative">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Text */}
            <div className="reveal-left">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-xs font-medium mb-6">
                <Star className="w-3.5 h-3.5" />
                Peer Reviews
              </div>
              <h2 className="text-4xl font-bold tracking-tight mb-6 leading-tight">
                Skills you can
                <br />
                <span className="bg-clip-text text-transparent bg-gradient-to-r from-emerald-400 to-teal-400">actually trust</span>
              </h2>
              <p className="text-muted-foreground text-lg leading-relaxed mb-8">
                Every skill on SkillForge can be reviewed and rated by real developers. Reviews are signed Nostr events — they can't be faked, deleted by a platform, or gamed by bots.
              </p>
              <div className="space-y-4">
                {[
                  { icon: <ShieldCheck className="w-4 h-4 text-emerald-400" />, text: 'Reviews are cryptographically signed by their authors' },
                  { icon: <Star className="w-4 h-4 text-emerald-400" />, text: 'Aggregate ratings surface the most reliable tools' },
                  { icon: <UserCheck className="w-4 h-4 text-emerald-400" />, text: 'Community feedback guides continuous improvement' },
                ].map(({ icon, text }) => (
                  <div key={text} className="flex items-start gap-3">
                    <div className="mt-0.5 w-7 h-7 rounded-lg bg-emerald-500/10 flex items-center justify-center shrink-0">{icon}</div>
                    <p className="text-sm text-muted-foreground leading-relaxed">{text}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Live skill cards */}
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-br from-emerald-500/10 to-teal-500/5 rounded-3xl blur-2xl" />
              <div className="relative">
                <LiveSkillCards />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Web of Trust Section */}
      <section ref={wotRef} className="py-28 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-indigo-500/[0.03] to-transparent" />
        <div className="absolute left-0 top-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-gradient-to-r from-indigo-600/10 to-transparent rounded-full blur-[100px] pointer-events-none" />

        <div className="container mx-auto px-4 relative">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* WoT graph mockup */}
            <div className="relative order-last lg:order-first reveal-left">
              <div className="absolute inset-0 bg-gradient-to-br from-indigo-500/10 to-purple-500/5 rounded-3xl blur-2xl" />
              <div className="relative glass-card rounded-3xl p-8 overflow-hidden">
                {/* Network graph SVG illustration */}
                <svg viewBox="0 0 320 240" className="w-full" fill="none" xmlns="http://www.w3.org/2000/svg">
                  {/* Edges */}
                  <line x1="160" y1="120" x2="80" y2="60" stroke="rgba(99,102,241,0.3)" strokeWidth="1.5" />
                  <line x1="160" y1="120" x2="240" y2="60" stroke="rgba(99,102,241,0.3)" strokeWidth="1.5" />
                  <line x1="160" y1="120" x2="60" y2="170" stroke="rgba(99,102,241,0.2)" strokeWidth="1" />
                  <line x1="160" y1="120" x2="260" y2="170" stroke="rgba(99,102,241,0.2)" strokeWidth="1" />
                  <line x1="160" y1="120" x2="160" y2="210" stroke="rgba(99,102,241,0.2)" strokeWidth="1" />
                  <line x1="80" y1="60" x2="240" y2="60" stroke="rgba(99,102,241,0.15)" strokeWidth="1" />
                  <line x1="80" y1="60" x2="60" y2="170" stroke="rgba(99,102,241,0.15)" strokeWidth="1" />
                  <line x1="240" y1="60" x2="260" y2="170" stroke="rgba(99,102,241,0.15)" strokeWidth="1" />
                  {/* Trust strength lines (thicker) */}
                  <line x1="160" y1="120" x2="80" y2="60" stroke="rgba(99,102,241,0.5)" strokeWidth="2.5" strokeDasharray="4 2" />
                  <line x1="160" y1="120" x2="240" y2="60" stroke="rgba(99,102,241,0.5)" strokeWidth="2.5" strokeDasharray="4 2" />
                  {/* Glow pulses */}
                  <circle cx="160" cy="120" r="28" fill="rgba(99,102,241,0.08)" />
                  <circle cx="80" cy="60" r="18" fill="rgba(99,102,241,0.06)" />
                  <circle cx="240" cy="60" r="18" fill="rgba(99,102,241,0.06)" />
                  {/* Center node — YOU */}
                  <circle cx="160" cy="120" r="20" fill="rgba(99,102,241,0.25)" stroke="rgba(99,102,241,0.6)" strokeWidth="1.5" />
                  <text x="160" y="125" textAnchor="middle" fontSize="9" fill="rgba(200,200,255,0.9)" fontWeight="600">YOU</text>
                  {/* Trusted peers */}
                  <circle cx="80" cy="60" r="14" fill="rgba(16,185,129,0.2)" stroke="rgba(16,185,129,0.5)" strokeWidth="1.5" />
                  <text x="80" y="65" textAnchor="middle" fontSize="8" fill="rgba(180,255,220,0.9)">alice</text>
                  <circle cx="240" cy="60" r="14" fill="rgba(16,185,129,0.2)" stroke="rgba(16,185,129,0.5)" strokeWidth="1.5" />
                  <text x="240" y="65" textAnchor="middle" fontSize="8" fill="rgba(180,255,220,0.9)">bob</text>
                  {/* Extended network */}
                  <circle cx="60" cy="170" r="10" fill="rgba(99,102,241,0.1)" stroke="rgba(99,102,241,0.3)" strokeWidth="1" />
                  <text x="60" y="174" textAnchor="middle" fontSize="7" fill="rgba(180,180,220,0.7)">carl</text>
                  <circle cx="260" cy="170" r="10" fill="rgba(99,102,241,0.1)" stroke="rgba(99,102,241,0.3)" strokeWidth="1" />
                  <text x="260" y="174" textAnchor="middle" fontSize="7" fill="rgba(180,180,220,0.7)">dana</text>
                  <circle cx="160" cy="210" r="10" fill="rgba(99,102,241,0.1)" stroke="rgba(99,102,241,0.3)" strokeWidth="1" />
                  <text x="160" y="214" textAnchor="middle" fontSize="7" fill="rgba(180,180,220,0.7)">eve</text>
                  {/* Legend */}
                  <circle cx="24" cy="16" r="5" fill="rgba(16,185,129,0.3)" stroke="rgba(16,185,129,0.6)" strokeWidth="1" />
                  <text x="33" y="20" fontSize="8" fill="rgba(180,255,220,0.7)">direct trust</text>
                  <circle cx="24" cy="30" r="5" fill="rgba(99,102,241,0.15)" stroke="rgba(99,102,241,0.4)" strokeWidth="1" />
                  <text x="33" y="34" fontSize="8" fill="rgba(180,180,220,0.6)">extended network</text>
                </svg>
                <div className="mt-2 text-center text-xs text-muted-foreground">
                  Skills vouched for by people you follow float to the top
                </div>
              </div>
            </div>

            {/* Text */}
            <div className="reveal-right">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-indigo-500/10 border border-indigo-500/20 text-indigo-400 text-xs font-medium mb-6">
                <Network className="w-3.5 h-3.5" />
                Web of Trust
              </div>
              <h2 className="text-4xl font-bold tracking-tight mb-6 leading-tight">
                Trust flows through
                <br />
                <span className="bg-clip-text text-transparent bg-gradient-to-r from-indigo-400 to-purple-400">your social graph</span>
              </h2>
              <p className="text-muted-foreground text-lg leading-relaxed mb-8">
                SkillForge leverages Nostr's web of trust. Skills reviewed by developers you already follow are ranked higher for you — turning your social graph into a personal quality filter.
              </p>
              <div className="space-y-4">
                {[
                  { icon: <UserCheck className="w-4 h-4 text-indigo-400" />, text: 'Your follows and their follows form your trust horizon' },
                  { icon: <Network className="w-4 h-4 text-indigo-400" />, text: "No central authority decides what's trustworthy" },
                  { icon: <ShieldCheck className="w-4 h-4 text-indigo-400" />, text: "Sybil-resistant: astroturfing can't beat real relationships" },
                ].map(({ icon, text }) => (
                  <div key={text} className="flex items-start gap-3">
                    <div className="mt-0.5 w-7 h-7 rounded-lg bg-indigo-500/10 flex items-center justify-center shrink-0">{icon}</div>
                    <p className="text-sm text-muted-foreground leading-relaxed">{text}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Open Source Section */}
      <section ref={openSrcRef} className="py-28 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-amber-500/[0.025] to-transparent" />
        <div className="absolute right-0 top-1/4 w-[600px] h-[600px] bg-gradient-to-l from-amber-600/8 to-transparent rounded-full blur-[120px] pointer-events-none" />

        <div className="container mx-auto px-4 relative">
          {/* Section header */}
          <div className="text-center mb-16 reveal">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-amber-500/10 border border-amber-500/20 text-amber-400 text-xs font-medium mb-6">
              <Code2 className="w-3.5 h-3.5" />
              Open Source First
            </div>
            <h2 className="text-4xl font-bold tracking-tight mb-5">
              Transparent by design.
              <br />
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-amber-400 to-orange-400">Auditable by anyone.</span>
            </h2>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto leading-relaxed">
              Before you let an AI agent run a skill, you deserve to know exactly what it does. SkillForge champions open-source skills and surfaces the information you need to make an informed decision.
            </p>
          </div>

          {/* Three pillar cards */}
          <div className="grid md:grid-cols-3 gap-5">
            <div className="group glass-card rounded-2xl p-6 hover:glass-card-hover transition-all duration-300 reveal reveal-delay-100">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-500/20 to-orange-500/20 flex items-center justify-center mb-5 border border-white/[0.08] group-hover:scale-105 transition-transform duration-300">
                <GitBranch className="w-5 h-5 text-amber-400" />
              </div>
              <h3 className="font-semibold text-lg mb-2">Source-linked</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">
                Skills can link directly to their repository. Reviewers can verify the code matches the published artifact before endorsing it.
              </p>
              <div className="mt-4 flex items-center gap-1.5 text-xs text-amber-400/70">
                <Eye className="w-3.5 h-3.5" />
                <span>Verified source links</span>
              </div>
            </div>

            <div className="group glass-card rounded-2xl p-6 hover:glass-card-hover transition-all duration-300 reveal reveal-delay-200">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-orange-500/20 to-red-500/20 flex items-center justify-center mb-5 border border-white/[0.08] group-hover:scale-105 transition-transform duration-300">
                <Lock className="w-5 h-5 text-orange-400" />
              </div>
              <h3 className="font-semibold text-lg mb-2">Immutable provenance</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">
                Every version is a signed Nostr event. The author's identity is permanently attached — no one can quietly swap in malicious code.
              </p>
              <div className="mt-4 flex items-center gap-1.5 text-xs text-orange-400/70">
                <ShieldCheck className="w-3.5 h-3.5" />
                <span>Cryptographic authorship</span>
              </div>
            </div>

            <div className="group glass-card rounded-2xl p-6 hover:glass-card-hover transition-all duration-300 reveal reveal-delay-300">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-500/20 to-teal-500/20 flex items-center justify-center mb-5 border border-white/[0.08] group-hover:scale-105 transition-transform duration-300">
                <Globe className="w-5 h-5 text-emerald-400" />
              </div>
              <h3 className="font-semibold text-lg mb-2">Censorship-resistant</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">
                Skills live on Nostr relays. No company can take them down, no legal threat can silently remove a tool your agents depend on.
              </p>
              <div className="mt-4 flex items-center gap-1.5 text-xs text-emerald-400/70">
                <Globe className="w-3.5 h-3.5" />
                <span>Decentralised storage</span>
              </div>
            </div>
          </div>

          {/* Code snippet callout */}
          <div className="mt-10 glass rounded-2xl p-6 border border-amber-500/[0.12] relative overflow-hidden reveal reveal-delay-400">
            <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-amber-500/30 to-transparent" />
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
              <div className="w-10 h-10 rounded-xl bg-amber-500/10 flex items-center justify-center shrink-0 border border-amber-500/20">
                <Code2 className="w-5 h-5 text-amber-400" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium mb-1">Skills are just signed Nostr events</p>
                <p className="text-xs text-muted-foreground leading-relaxed">
                  There's no proprietary format. A skill is a <code className="px-1 py-0.5 rounded bg-white/[0.05] text-amber-300/80 text-[11px]">kind:38473</code> addressable event — readable, auditable, and relay-portable by any Nostr client.
                </p>
              </div>
              <Link
                to="/nip"
                className="shrink-0 text-xs text-amber-400/80 hover:text-amber-400 transition-colors flex items-center gap-1"
              >
                Learn more
                <ArrowRight className="w-3 h-3" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Recent Skills */}
      <section ref={recentRef} className="py-24 relative">
        <div className="absolute left-0 top-1/2 -translate-y-1/2 w-[400px] h-[400px] bg-gradient-to-r from-amber-600/10 to-transparent rounded-full blur-[100px] pointer-events-none" />
        <div className="container mx-auto px-4 relative">
          <div className="flex items-center justify-between mb-10 reveal">
            <div>
              <h2 className="text-3xl font-bold mb-2">Recent Skills</h2>
              <p className="text-muted-foreground">Latest skills published by the community</p>
            </div>
            <Link to="/browse">
              <Button variant="ghost" className="gap-2 text-muted-foreground hover:text-foreground">
                View all
                <ArrowRight className="w-4 h-4" />
              </Button>
            </Link>
          </div>

          <div className="reveal reveal-delay-200">
            <SkillBrowser limit={6} compact />
          </div>
        </div>
      </section>

      {/* Connect Agent Banner */}
      <section ref={ctaRef} className="py-16 relative overflow-hidden">
        <div className="container mx-auto px-4 relative">
          <div className="reveal-scale glass-card rounded-3xl p-8 sm:p-10 relative overflow-hidden">
            <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-orange-500/40 to-transparent" />
            <div className="absolute inset-0 bg-gradient-to-br from-orange-500/[0.06] via-amber-500/[0.03] to-transparent" />
            <div className="relative flex flex-col sm:flex-row items-start sm:items-center gap-6">
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-orange-500/20 to-amber-500/20 flex items-center justify-center border border-white/[0.08] shrink-0">
                <Bot className="w-7 h-7 text-orange-400" />
              </div>
              <div className="flex-1 min-w-0">
                <h2 className="text-xl font-bold mb-1">Ready to connect your agent?</h2>
                <p className="text-muted-foreground text-sm leading-relaxed">
                  Log in and we'll generate a ready-to-paste config pre-loaded with your npub as the WoT trust anchor — your agent inherits your full social graph in seconds.
                </p>
              </div>
              <Link to="/connect-agent" className="shrink-0">
                <Button className="gap-2 bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-400 hover:to-amber-400 border-0 shadow-lg shadow-orange-500/20 whitespace-nowrap">
                  <Bot className="w-4 h-4" />
                  Connect Agent
                  <ArrowRight className="w-4 h-4" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section ref={ctaRef} className="py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-t from-orange-500/[0.05] via-transparent to-transparent" />
        <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-gradient-to-t from-orange-600/20 via-amber-500/10 to-transparent rounded-full blur-[100px] pointer-events-none" />
        
        <div className="container mx-auto px-4 relative">
          <div className="max-w-2xl mx-auto text-center reveal-scale">
            <h2 className="text-4xl font-bold mb-6">Ready to share your work?</h2>
            <p className="text-lg text-muted-foreground mb-10">
              Connect your Nostr identity and publish your first skill in minutes.
            </p>
            {user ? (
              <Link to="/publish">
                <Button size="lg" className="gap-2 bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-400 hover:to-amber-400 border-0 shadow-xl shadow-orange-500/25 text-base">
                  <Plus className="w-5 h-5" />
                  Publish Your First Skill
                </Button>
              </Link>
            ) : (
              <LoginArea />
            )}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-6 border-t border-white/[0.06]">
        <div className="container mx-auto px-4">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3 text-sm text-muted-foreground">
            <div className="flex items-center gap-2.5">
              <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-orange-500/20 to-emerald-500/20 flex items-center justify-center border border-white/[0.08]">
                <Terminal className="w-3.5 h-3.5 text-orange-400" />
              </div>
              <span>SkillForge</span>
            </div>
            <div className="flex items-center gap-6">
              <a href="https://nostr.com" target="_blank" rel="noopener noreferrer" className="hover:text-foreground transition-colors">
                Nostr
              </a>
              <a href="https://cashu.space" target="_blank" rel="noopener noreferrer" className="hover:text-foreground transition-colors">
                Cashu
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

// ─── Hero skills preview ──────────────────────────────────────────────────────

function HeroSkillsPreview() {
  const { data: skills, isLoading } = useSkills({ limit: 3 });

  if (isLoading) {
    return (
      <div className="glass-card rounded-2xl p-5 space-y-3">
        {Array.from({ length: 3 }).map((_, i) => (
          <div key={i} className="flex items-center gap-3">
            <Skeleton className="w-10 h-10 rounded-xl shrink-0" />
            <div className="flex-1 space-y-1.5">
              <Skeleton className="h-4 w-32" />
              <Skeleton className="h-3 w-48" />
            </div>
            <Skeleton className="h-5 w-12 rounded-full shrink-0" />
          </div>
        ))}
      </div>
    );
  }

  const preview = skills?.slice(0, 3) ?? [];

  if (preview.length === 0) return null;

  return (
    <div className="glass-card rounded-2xl overflow-hidden shadow-2xl shadow-black/40">
      {/* Title bar */}
      <div className="flex items-center justify-between px-5 py-3.5 bg-gradient-to-b from-white/[0.05] to-white/[0.02] border-b border-white/[0.06]">
        <div className="flex items-center gap-2 text-xs text-muted-foreground font-mono">
          <Terminal className="w-3.5 h-3.5 text-orange-400" />
          skillforge — latest
        </div>
        <Link to="/browse" className="text-xs text-orange-400/80 hover:text-orange-400 transition-colors flex items-center gap-1">
          View all <ArrowRight className="w-3 h-3" />
        </Link>
      </div>
      <div className="divide-y divide-white/[0.04]">
        {preview.map((skill) => (
          <HeroSkillRow key={skill.id} skill={skill} />
        ))}
      </div>
    </div>
  );
}

function HeroSkillRow({ skill }: { skill: Skill }) {
  const author = useAuthor(skill.pubkey);
  const authorName = author.data?.metadata?.name || genUserName(skill.pubkey);

  return (
    <Link to={`/skill/${skill.pubkey}/${skill.identifier}`} className="flex items-center gap-4 px-5 py-4 hover:bg-white/[0.03] transition-colors group">
      <div className={`w-10 h-10 shrink-0 rounded-xl flex items-center justify-center border border-white/[0.08] group-hover:scale-105 transition-transform ${skill.type === 'mcp' ? 'bg-gradient-to-br from-emerald-500/20 to-green-500/20 text-emerald-400' : 'bg-gradient-to-br from-orange-500/20 to-amber-500/20 text-orange-400'}`}>
        {skill.type === 'mcp' ? <Server className="w-4 h-4" /> : <Package className="w-4 h-4" />}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium truncate group-hover:text-orange-400 transition-colors">{skill.name}</p>
        <p className="text-xs text-muted-foreground truncate">by {authorName} · v{skill.version}</p>
      </div>
      <Badge variant="secondary" className={`shrink-0 text-xs ${skill.price > 0 ? 'bg-gradient-to-r from-orange-500/80 to-amber-500/80 border-0 text-white' : 'bg-white/[0.05] border-white/[0.08]'}`}>
        {skill.price > 0 && <Zap className="w-3 h-3 mr-1" />}
        {formatPrice(skill.price)}
      </Badge>
    </Link>
  );
}

// ─── Live skill cards in the trust section ────────────────────────────────────

function LiveSkillCards() {
  const { data: skills, isLoading } = useSkills({ limit: 3 });

  if (isLoading) {
    return (
      <div className="space-y-3">
        {Array.from({ length: 3 }).map((_, i) => (
          <Skeleton key={i} className="h-24 rounded-2xl" />
        ))}
      </div>
    );
  }

  const cards = skills?.slice(0, 3) ?? [];

  if (cards.length === 0) return null;

  return (
    <div className="space-y-3">
      {cards.map((skill, i) => (
        <LiveSkillCard
          key={skill.id}
          skill={skill}
          className={`reveal ${['reveal-delay-100', 'reveal-delay-200', 'reveal-delay-300'][i]}`}
          offset={i % 2 === 1}
        />
      ))}
    </div>
  );
}

function LiveSkillCard({ skill, className = '', offset }: { skill: Skill; className?: string; offset?: boolean }) {
  const author = useAuthor(skill.pubkey);
  const meta = author.data?.metadata;
  const authorName = meta?.name || genUserName(skill.pubkey);
  const npub = nip19.npubEncode(skill.pubkey);

  return (
    <Link
      to={`/skill/${skill.pubkey}/${skill.identifier}`}
      className={`block glass-card rounded-2xl p-4 transition-all duration-300 hover:glass-card-hover ${className}`}
      style={offset ? { transform: 'translateX(12px)' } : undefined}
    >
      <div className="flex items-start justify-between gap-3 mb-2">
        <div className="flex items-center gap-2.5 min-w-0">
          <div className={`w-8 h-8 shrink-0 rounded-xl flex items-center justify-center border border-white/[0.08] ${skill.type === 'mcp' ? 'bg-gradient-to-br from-emerald-500/20 to-green-500/20 text-emerald-400' : 'bg-gradient-to-br from-orange-500/20 to-amber-500/20 text-orange-400'}`}>
            {skill.type === 'mcp' ? <Server className="w-4 h-4" /> : <Package className="w-4 h-4" />}
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-1.5">
              <span className="text-xs font-medium truncate">{skill.name}</span>
              <ShieldCheck className="w-3 h-3 text-emerald-400 shrink-0" />
            </div>
            <Link
              to={`/profile/${npub}`}
              onClick={(e) => e.stopPropagation()}
              className="flex items-center gap-1 text-[10px] text-muted-foreground hover:text-orange-400 transition-colors"
            >
              <Avatar className="w-3.5 h-3.5">
                <AvatarImage src={meta?.picture} />
                <AvatarFallback className="text-[8px]">{authorName[0]?.toUpperCase()}</AvatarFallback>
              </Avatar>
              {authorName}
            </Link>
          </div>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          {Array.from({ length: 5 }).map((_, s) => (
            <Star key={s} className="w-3 h-3 text-amber-400/40" />
          ))}
        </div>
      </div>
      <p className="text-xs text-muted-foreground leading-relaxed line-clamp-2">{skill.description}</p>
    </Link>
  );
}

// ─── Feature card ─────────────────────────────────────────────────────────────

function FeatureCard({ icon, title, description, gradient }: { icon: React.ReactNode; title: string; description: string; gradient: string }) {
  return (
    <div className="group relative p-6 rounded-2xl glass-card hover:glass-card-hover transition-all duration-300 cursor-default">
      <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${gradient} flex items-center justify-center mb-5 text-white/80 border border-white/[0.08] group-hover:scale-105 transition-transform duration-300`}>
        {icon}
      </div>
      <h3 className="font-semibold text-lg mb-2">{title}</h3>
      <p className="text-sm text-muted-foreground leading-relaxed">{description}</p>
    </div>
  );
}

export default Index;
